/**
 * Agents UI hooks — TanStack Query over the agents client resolved by `useAgentsClient`
 * (fixture by default, the live agent-trust endpoints when VITE_AGENTS_LIVE=true). The
 * client `scope` (workspace id, or "fixtures") is folded into every key so the two sources
 * never collide and a workspace switch refetches. The component API is unchanged.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAgentsClient } from "./useAgentsClient";
import type { Autonomy } from "./fixtures";
import type { SimWindow } from "./fixtures-deep";

const ROOT = "agents-ui";
const key = (scope: string, ...rest: unknown[]) =>
  [ROOT, scope, ...rest] as const;

// Live polling — when VITE_AGENTS_LIVE is on, the read hooks (list, runs, running-now,
// activity, notifications) poll every 2.5s so create→watch→finish needs no manual refresh.
// Gated so fixtures never churn (refetchInterval stays false off the live path).
const LIVE = import.meta.env.VITE_AGENTS_LIVE === "true";
const LIVE_REFETCH: number | false = LIVE ? 2500 : false;

export function useAgentsList() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "list"),
    queryFn: () => client.listAgents(),
    refetchInterval: LIVE_REFETCH,
  });
}

/** In-flight runs across all agents — the "Running now" strip (live run-status feed). */
export function useRunningNow() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "running-now"),
    queryFn: () => client.listRunningNow(),
    refetchInterval: LIVE_REFETCH,
  });
}

export function useTemplates() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "templates"),
    queryFn: () => client.listTemplates(),
  });
}

export function useSetAgentEnabled() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      client.setEnabled(v.id, v.enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useCreateAgent() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purpose: string) => client.createAgent(purpose),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useDeleteAgent() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.deleteAgent(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
      void qc.invalidateQueries({ queryKey: key(scope, "approvals") });
      void qc.invalidateQueries({ queryKey: key(scope, "activity") });
    },
  });
}

export function useSetAgentColor() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; color: string }) =>
      client.setColor(v.id, v.color),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useSetAgentAutonomy() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; autonomy: Autonomy }) =>
      client.setAutonomy(v.id, v.autonomy),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useRuns(agentId: string) {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "runs", agentId),
    queryFn: () => client.listRuns(agentId),
    refetchInterval: LIVE_REFETCH,
  });
}

/** Simulate is on-demand (a button), so it's a mutation that returns the result. */
export function useSimulate(agentId: string) {
  const { client } = useAgentsClient();
  return useMutation({
    mutationFn: (window: SimWindow) => client.simulate(agentId, window),
  });
}

export function useApprovals() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "approvals"),
    queryFn: () => client.listApprovals(),
    refetchInterval: LIVE_REFETCH,
  });
}

export function useDecideApproval() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      decision: "approved" | "edited" | "rejected";
      preparedPayload?: Record<string, unknown>;
    }) => client.decideApproval(v.id, v.decision, v.preparedPayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "approvals") });
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
      // Refresh runs so an inline sign-off card in a run timeline clears once decided.
      void qc.invalidateQueries({ queryKey: key(scope, "runs") });
      // Refresh notifications so a resolved sign_off signal drops from the feed/count.
      void qc.invalidateQueries({ queryKey: key(scope, "notifications") });
    },
  });
}

export function useActivity() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "activity"),
    queryFn: () => client.listActivity(),
    refetchInterval: LIVE_REFETCH,
  });
}

/** The three typed notification signals (design §4); the feed sorts them by priority. */
export function useNotifications() {
  const { client, scope } = useAgentsClient();
  return useQuery({
    queryKey: key(scope, "notifications"),
    queryFn: () => client.listNotifications(),
    refetchInterval: LIVE_REFETCH,
  });
}

export function useApplyRefinement() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { agentId: string; condition: string }) =>
      client.applyChatRefinement(v.agentId, v.condition),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}

export function useUndoRefinement() {
  const { client, scope } = useAgentsClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => client.undoRefinement(agentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(scope, "list") });
    },
  });
}
